import React, { useState, useEffect } from 'react';
// import SubscribeButton from '../components/SubscribeButton';
import { supabase } from '../util/supabaseClient';
import { useAppContext } from '../context/AppContext';
import { useApiContext } from '../context/ApiContext';

export default function Price() {
    // userId는 실제 인증 시스템에서 가져와야 합니다
    const { session, isLoading: authLoading } = useAppContext();
    const { api, isLoading: apiLoading } = useApiContext();
    const userId = session && session.user.id;

    const [hasUsedTrial, setHasUsedTrial] = useState(false);
    const [hasAccess, setHasAccess] = useState(false);
    const [loading, setLoading] = useState(true);
    const [freeTrialActive, setFreeTrialActive] = useState(false);
    const [currentSubscription, setCurrentSubscription] = useState(null);
    
    // 무료체험 및 구독 정보 확인
    useEffect(() => {
        async function checkSubscriptionStatus() {
            if (!userId) {
                setLoading(false);
                return;
            }
            
            try {
                // API Context를 사용하여 무료체험 정보 확인
                const { data: freeTrialData, error: freeTrialError } = await api.checkFreeTrial(userId);
                
                if (!freeTrialError && freeTrialData) {
                    // 무료체험 사용 가능 여부 설정
                    // eligible이 false인 경우: 이미 사용 중이거나 이전에 사용한 경우
                    setHasUsedTrial(!freeTrialData.eligible);
                    
                    // 무료체험 현재 사용 중인지 확인
                    // status가 'trialing'인 경우: 현재 활성화된 무료체험이 있음 
                    setFreeTrialActive(freeTrialData.status === 'trialing' || false);
                }
                
                // 구독 정보 확인 (무료체험 외의 유료 구독)
                const { data: subscription, error: subscriptionError } = await supabase
                    .from('subscriptions')
                    .select('*')
                    .eq('user_id', userId)
                    .eq('status', 'active')
                    .neq('plan_name', 'FREE_TRIAL')
                    .maybeSingle();  // null 반환 허용

                if (subscriptionError && subscriptionError.code !== 'PGRST116') {
                    console.error('구독 정보 확인 중 오류:', subscriptionError);
                } else if (subscription) {
                    // 현재 구독 정보 저장
                    setCurrentSubscription(subscription);
                }
                
                // 유효한 구독이 있는지 확인
                const hasValidSubscription = subscription && subscription.status === 'active';
                
                // 무료체험 중이거나 유효한 구독이 있으면 접근 권한 부여
                setHasAccess(freeTrialActive || hasValidSubscription);
            } catch (error) {
                console.error('구독 상태 확인 중 오류:', error);
            } finally {
                setLoading(false);
            }
        }
        
        checkSubscriptionStatus();
    }, [userId, freeTrialActive, api]);
    
    const plans = [
        {
            name: 'Standard',
            price: '$7.99',
            priceId: process.env.REACT_APP_PRICE_ID_GU,
            description: '시/군/구별 기준 모든 아파트 검색 가능',
            features: [
                '매매, 전세 최저가 매물 정보',
                'CSV 다운로드 가능',
                '서울시 강남구, 송파구 등 시/군/구 단위 검색',
                '최고가, 하락률, 단지별 상세 정보 조회',
                '하루 1회 전국 아파트 데이터 업데이트',
                '복잡한 쿼리 요청을 통한 정밀한 맞춤형 데이터 제공',
            ],
            buttonText: 'Standard 플랜 시작하기',
        }
    ];

    const handleSubscribe = async (plan) => {
        try {
          const { data, error } = await api.createCheckoutSession(
            plan.priceId, 
            userId, 
            plan.name, 
            plan.price
          );
          
          if (error) {
            console.error('결제 세션 생성 실패:', error);
            return;
          }
          console.log(data, 'data');
          // Stripe Checkout 페이지로 이동
          window.location.href = data.url;
        } catch (err) {
          console.error(err);
        }
    };
    
    const handleFreeTrial = async () => {
        if (!userId) {
            alert('로그인이 필요한 서비스입니다.');
            return;
        }
        
        if (hasUsedTrial) {
            alert('이미 무료체험을 이용하셨습니다.');
            return;
        }
        
        try {
            const { data, error } = await api.activateFreeTrial(userId);
            
            if (error) {
                // 상세 에러 메시지 처리
                alert(error.message || '무료체험 활성화 중 오류가 발생했습니다.');
                
                // 무료체험을 이미 사용했으면 상태 업데이트
                if (error.message.includes('이미 무료체험을 사용하셨습니다')) {
                    setHasUsedTrial(true);
                }
                return;
            }
            
            alert('무료체험이 활성화되었습니다!');
            window.location.href = '/dashboard'; // 사용자를 대시보드로 리디렉션
        } catch (err) {
            console.error('무료체험 활성화 중 오류:', err);
            alert('서버 연결 중 오류가 발생했습니다. 다시 시도해 주세요.');
        }
    };
    

    return (
        <div className="bg-[#FFFEF6] flex justify-center text-gray-800 p-2 md:p-2">
            <div className="w-full max-w-xl mx-auto px-4 md:px-0">
                <h1 className="text-2xl md:text-4xl font-bold mb-6 md:mb-12 text-center">나에게 맞는 플랜을 선택해보세요.</h1>

                {/* 무료체험 안내 */}
                {!loading && !hasUsedTrial && !currentSubscription && (
                    <div className="flex justify-center my-6 md:my-12">
                        <div className="bg-[#FFFEF6] flex flex-col md:flex-row md:justify-around items-center p-4 rounded-lg text-center shadow-lg w-full max-w-2xl hover:bg-[#fffdec]">
                            <span className="mb-3 md:mb-0">Standard 플랜을 일주일 동안 무료로 사용해 보세요.</span>
                            <button 
                                onClick={handleFreeTrial}
                                className="px-4 py-2 text-green-600 font-semibold transform hover:scale-110 transition-transform duration-200">
                                무료체험 이용하기
                            </button>
                        </div>
                    </div>
                )}

                {/* 현재 구독 중인 정보 표시 */}
                {!loading && currentSubscription && (
                    <div className="flex justify-center my-6 md:my-12">
                        <div className="bg-green-50 border-green-200 border p-4 rounded-lg text-center shadow-lg w-full max-w-2xl">
                            <p className="text-green-700 font-medium">
                                현재 <span className="font-bold">{currentSubscription.plan_name}</span> 플랜을 이용 중입니다.
                            </p>
                            <p className="text-gray-600 text-sm mt-2">
                                이용기간: {new Date(currentSubscription.current_period_start).toLocaleDateString()} ~ {new Date(currentSubscription.current_period_end).toLocaleDateString()}
                            </p>
                        </div>
                    </div>
                )}

                {/* Pricing Cards */}
                <div className="flex flex-col md:flex-row justify-center gap-6 w-full">
                    {plans.map((plan) => {
                        const isCurrentPlan = currentSubscription && currentSubscription.plan_name === plan.name;
                        
                        return (
                            <div
                                onClick={isCurrentPlan ? undefined : () => handleSubscribe(plan)}
                                key={plan.name} 
                                className={`rounded-lg border p-4 md:p-6 shadow-md flex-1 ${
                                    isCurrentPlan ? 
                                    'bg-[#FFFEF6] border-green-500 border-2 cursor-default' : 
                                    'bg-[#FFFEF6] cursor-pointer hover:bg-[#fffdec] hover:scale-105 transition-all duration-200'
                                }`}>
                                <h2 className="text-xl md:text-2xl font-bold mb-2">{plan.name}</h2>
                                {isCurrentPlan && (
                                    <div className="bg-green-100 text-green-800 text-xs font-semibold px-2.5 py-0.5 rounded-full inline-block mb-2">
                                        현재 구독중
                                    </div>
                                )}
                                <p className="text-lg md:text-xl font-semibold mb-3 md:mb-4">{plan.price} / 월</p>
                                <p className="text-base md:text-lg font-semibold text-green-600 mb-3 md:mb-4">{plan.description}</p>
                                <ul className="text-xs md:text-sm mb-5 md:mb-6 list-disc list-inside space-y-1 text-gray-700">
                                    {plan.features.map((feature, index) => (
                                        <li key={index}>{feature}</li>
                                    ))}
                                </ul>
                                {isCurrentPlan ? (
                                    <button 
                                        className="w-full bg-gray-300 text-gray-700 px-3 py-2 rounded-md cursor-default text-sm md:text-base">
                                        현재 이용중인 플랜
                                    </button>
                                ) : (
                                    <button 
                                        className="w-full bg-green-600 text-white px-3 py-2 rounded-md hover:bg-green-700 transition-colors text-sm md:text-base">
                                        {plan.buttonText}
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

